import React, { useCallback } from 'react';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  Button, ButtonText, HStack, Spinner, Text,
} from '@gluestack-ui/themed';

import { useStudentExamAbortConfirm } from '@/student/module/utils/studentExamAbortConfirm';

export type StudentExamControlBarProps = {
  onAbort: () => void | Promise<void>;
  isAborting?: boolean;
  /** When false (dry run), abort confirm omits failed-attempt wording. Default true. */
  requiresEvaluation?: boolean;
  /** When true (exam formally begun), abort confirm submits for grading instead of failed-attempt. */
  committed?: boolean;
  /**
   * Optional non-destructive leave (waiting phase only): exits the room without
   * aborting the run, so the student keeps their booking and can rejoin. Distinct
   * from Abort, which ends the exam.
   */
  onLeave?: () => void;
};

/**
 * Minimal in-exam controls for students: abort, plus an optional non-destructive
 * leave while still waiting (no mic/cam/screen toggles).
 */
export function StudentExamControlBar({
  onAbort,
  isAborting = false,
  requiresEvaluation = true,
  committed = false,
  onLeave,
}: StudentExamControlBarProps) {
  const { t } = useTranslation();
  const confirmAbort = useStudentExamAbortConfirm();

  const handleOpenDialog = useCallback(async () => {
    if (isAborting) return;
    const confirmed = await confirmAbort(t, requiresEvaluation, committed);
    if (confirmed) await onAbort();
  }, [committed, confirmAbort, isAborting, onAbort, requiresEvaluation, t]);

  return (
    <HStack
      justifyContent="center"
      alignItems="center"
      space="md"
      py="$3"
      px="$4"
      testID="student-exam-control-bar"
    >
      {onLeave ? (
        <Button
          variant="outline"
          size="sm"
          action="secondary"
          onPress={onLeave}
          isDisabled={isAborting}
          testID="student-exam-leave"
        >
          <ButtonText>{t('oralExam.leaveExam')}</ButtonText>
        </Button>
      ) : null}
      <Pressable
        onPress={handleOpenDialog}
        disabled={isAborting}
        testID="student-exam-abort"
      >
        <HStack
          px="$4"
          py="$2"
          borderRadius="$full"
          bg="$statusError"
          alignItems="center"
          space="xs"
          opacity={isAborting ? 0.6 : 1}
        >
          {isAborting ? (
            <Spinner size="small" color="$textInverted" />
          ) : null}
          <Text size="sm" color="$textInverted" fontWeight="semibold">
            {t('oralExam.student.abort.confirm')}
          </Text>
        </HStack>
      </Pressable>
    </HStack>
  );
}
